import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { usePartnerConfig } from './usePartnerConfig';

const HOW_IT_WORKS = [
  { step: '01', label: 'Start a party', body: 'One player hosts the game.' },
  { step: '02', label: 'Everyone joins', body: 'Friends join from their own phones.' },
  { step: '03', label: 'Play live', body: 'Vote, react, and watch it unfold in real time.' },
];

export default function PartnerLandingPage() {
  const { partnerId, eventId } = useParams<{
    partnerId: string;
    eventId?: string;
  }>();

  const { status, config } = usePartnerConfig(partnerId!, eventId);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (status === 'not_found' || !config) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-6">
        <div className="max-w-sm w-full text-center">
          <img src="/logo.png" alt="Kuze" className="h-12 w-auto mx-auto mb-10" />
          <h1 className="text-2xl font-bold mb-3">Nothing here.</h1>
          <p className="text-gray-400">Check the link and try again.</p>
        </div>
      </div>
    );
  }

  const ctaUrl =
    `https://kuze.app/play?partner=${config.partnerId}` +
    (config.eventId ? `&event=${config.eventId}` : '');

  const qrUrl =
    `https://kuze.app/play?partner=${config.partnerId}` +
    (config.eventId ? `&event=${config.eventId}` : '');

  const bgStyle = {
    background: `linear-gradient(135deg, ${config.brandColor}1E 0%, transparent 60%), #0a0a0f`,
  } as React.CSSProperties;

  return (
    <div
      className="min-h-screen text-white flex flex-col items-center px-6 py-10 gap-10"
      style={bgStyle}
    >
      {/* Header — partner logo */}
      <div className="flex flex-col items-center pt-4">
        <img
          src={config.logoUrl}
          alt={config.name}
          className="max-h-[52px] w-auto object-contain"
          onError={(e) => {
            const target = e.currentTarget as HTMLImageElement;
            target.style.display = 'none';
            const fallback = target.nextElementSibling as HTMLElement | null;
            if (fallback) fallback.style.display = 'block';
          }}
        />
        <p className="text-lg font-semibold mt-1 hidden" aria-hidden="true">
          {config.name}
        </p>
      </div>

      {/* QR code */}
      <div className="flex flex-col items-center gap-3">
        <div className="bg-white p-3 rounded-2xl shadow-lg">
          <QRCodeSVG
            value={qrUrl}
            size={160}
            bgColor="#ffffff"
            fgColor="#0a0a0f"
            level="M"
          />
        </div>
        <p className="text-gray-400 text-sm">Scan to join on your phone</p>
      </div>

      {/* Main */}
      <div className="max-w-sm w-full text-center">
        <h1 className="text-3xl font-bold mb-4 leading-tight">
          {config.landingHeadline}
        </h1>
        <p className="text-gray-300 text-lg mb-4">{config.landingSubtext}</p>
        <p className="text-gray-500 text-sm">Party games by Kuze</p>
      </div>

      {/* How it works */}
      <div className="max-w-sm w-full">
        <h2
          className="text-xl font-bold mb-6 text-center"
          style={{ color: config.brandColor }}
        >
          How it works
        </h2>
        <div className="flex justify-between gap-4">
          {HOW_IT_WORKS.map(({ step, label, body }) => (
            <div key={step} className="flex flex-col items-center text-center flex-1 gap-2">
              <span
                className="text-4xl font-black leading-none"
                style={{ color: config.brandColor }}
              >
                {step}
              </span>
              <p className="text-white font-semibold text-sm">{label}</p>
              <p className="text-gray-400 text-xs leading-snug">{body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-sm w-full">
        <a
          href={ctaUrl}
          className="flex items-center justify-center w-full py-4 rounded-xl font-semibold text-lg shadow-lg hover:opacity-90 transition-opacity"
          style={{
            backgroundColor: config.brandColor,
            color: config.buttonTextColor,
          }}
        >
          Start Playing
        </a>
      </div>
    </div>
  );
}
